import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { ColourSwatch } from "./ColourSwatch";
import { ProductPlaceholder } from "./ProductPlaceholder";
import { EnquiryDialog } from "./EnquiryDialog";
import { MessageCircle, Phone, ArrowUpRight } from "lucide-react";
import { imageUrl, whatsappLink, PHONE_TEL } from "../data/constants";

const SPEC_ROWS = [
  ["Material", "material"], ["Construction", "construction"], ["Thickness", "thickness"],
  ["Weight", "weight"], ["Backing", "backing"], ["Pack Size", "pack_size"],
];

export function QuickViewModal({ product, palette, open, onOpenChange }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  if (!product) return null;
  const img = imageUrl(product.primary_image || product.images?.[0], 900);
  const specs = SPEC_ROWS.filter(([, k]) => product[k]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto p-0" data-testid="product-quick-view-modal">
          <DialogTitle className="sr-only">{product.name}</DialogTitle>
          <DialogDescription className="sr-only">Quick view of {product.name} — sizes, colours, specifications and enquiry options.</DialogDescription>
          <div className="grid gap-0 md:grid-cols-2">
            <div className="flex items-center justify-center bg-slate-50 p-4">
              {img ? (
                <img src={img} alt={product.name} className="max-h-[70vh] w-full rounded-xl object-contain" />
              ) : (
                <ProductPlaceholder className="aspect-square w-full rounded-xl" />
              )}
            </div>
            <div className="p-6">
              <span className="inline-block rounded-full bg-[#0B1B3D] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                {product.category}
              </span>
              <h2 className="mt-3 font-display text-2xl font-extrabold text-[#0B1B3D]">{product.name}</h2>
              {product.description && <p className="mt-2 text-sm text-slate-600">{product.description}</p>}

              {product.sizes?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-900">Sizes</h4>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {product.sizes.map((s) => (
                      <span key={s} className="rounded-lg bg-slate-100 px-2.5 py-1 font-spec text-xs text-slate-700">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {product.colours?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-900">Colours</h4>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {product.colours.map((c) => (
                      <span key={c} className="flex items-center gap-1.5 rounded-full bg-slate-100 py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-700">
                        <ColourSwatch name={c} palette={palette} size="sm" /> {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {specs.length > 0 && (
                <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <tbody>
                      {specs.map(([label, k], i) => (
                        <tr key={k} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                          <td className="px-3 py-1.5 font-semibold text-slate-700">{label}</td>
                          <td className="px-3 py-1.5 font-spec text-slate-600">{product[k]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {product.features?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {product.features.map((f) => (
                    <span key={f} className="rounded-full bg-[#ECFDF5] px-2.5 py-0.5 text-[11px] font-medium text-[#059669] ring-1 ring-[#A7F3D0]">{f}</span>
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={() => setQuoteOpen(true)} data-testid="quick-view-quote-btn"
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#0B1B3D] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1E293B]">
                  Get Quote
                </button>
                <a href={whatsappLink({ product: product.name, size: product.sizes?.[0], colour: product.colours?.[0] })}
                  target="_blank" rel="noopener noreferrer" data-testid="quick-view-whatsapp-btn"
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366] text-white hover:scale-105">
                  <MessageCircle className="h-4 w-4" />
                </a>
                <a href={`tel:${PHONE_TEL}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-[#0B1B3D] hover:border-[#10B981]">
                  <Phone className="h-4 w-4" />
                </a>
              </div>
              <Link to={`/product/${product.id}`} onClick={() => onOpenChange(false)}
                className="mt-3 flex items-center gap-1 text-sm font-semibold text-[#059669] hover:gap-2">
                View full details <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <EnquiryDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={product} />
    </>
  );
}
